import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type FormEvent,
  type KeyboardEvent,
} from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion } from 'framer-motion';
import ReactMarkdown from 'react-markdown';
import {
  ArrowRight,
  Check,
  Download,
  FileText,
  Loader2,
  Mail,
  RefreshCw,
  Send,
  Share2,
} from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import {
  useSynthesisStream,
  type LayerKey,
  type LayerKind,
  type LayerState,
  type Source,
  type SynthesisState,
} from '../hooks/useSynthesisStream';
import { renderCitations } from './CitationLink';
import CelebrationBurst from './CelebrationBurst';
import './ChatWindow.css';

type AttachmentMeta = {
  fileName: string;
  mimeType: string;
};

export type PreloadedInsight = {
  id: number;
  headline: string;
  body: string;
  followups: string[];
  sources: Source[];
  attachments?: AttachmentMeta[];
};

// ── Types ─────────────────────────────────────────────────────────────
type SystemMsg = {
  kind: 'system';
  id: string;
  question: string;
  questionId: number;
  streamPath: string;
  at: number;
  /** When set, the SystemRow renders the preloaded insight directly and
   *  does NOT open EventSource. Used for hydrating saved thread state. */
  preloaded?: PreloadedInsight;
};

type Msg =
  | { kind: 'user'; id: string; text: string; at: number; attachments?: AttachmentMeta[] }
  | SystemMsg
  | { kind: 'error'; id: string; message: string; at: number };

type ResearchContext = {
  threadId: number;
  title: string;
};

type ResearchChatMsg = {
  role: 'user' | 'assistant';
  content: string;
};

type Props = {
  draftsOpen: boolean;
  onCloseDrafts: () => void;
  onRequestHydration?: (fn: (threadId: number) => Promise<void>) => void;
  onRequestReset?: (fn: () => void) => void;
  onRequestPrefill?: (fn: (text: string) => void) => void;
  /** When false the message stream is hidden — only the input bar renders. */
  streamVisible?: boolean;
  /** Fired when the user sends a question so the parent can switch views. */
  onSendTriggered?: () => void;
  /** When set, the chat bar enters "ask this research" mode. */
  researchContext?: ResearchContext | null;
  /** Called to exit research-chat mode. */
  onClearResearchContext?: () => void;
};

type InputMode = 'chat' | 'auth-email' | 'auth-code' | 'publish';

type PublishVisibility = 'private' | 'public' | 'anonymous';

const THREAD_MAX_QUESTIONS = 20;

function newId(): string {
  return Math.random().toString(36).slice(2, 10);
}

function formatTime(at: number): string {
  const d = new Date(at);
  return d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
}

// ── Root ──────────────────────────────────────────────────────────────
export default function ChatWindow({
  draftsOpen,
  onCloseDrafts,
  onRequestHydration,
  onRequestReset,
  onRequestPrefill,
  streamVisible = true,
  onSendTriggered,
  researchContext,
  onClearResearchContext,
}: Props) {
  const { user, loading: authLoading, requestOtp, verifyOtp } = useAuth();

  const [messages, setMessages] = useState<Msg[]>([]);
  const [input, setInput] = useState('');
  const [mode, setMode] = useState<InputMode>('chat');

  // Thread state
  const [threadId, setThreadId] = useState<number | null>(null);
  const [questionCount, setQuestionCount] = useState(0);
  const [threadPublished, setThreadPublished] = useState(false);

  // Publish state
  const [publishVisibility, setPublishVisibility] =
    useState<PublishVisibility>('private');
  const [publishBusy, setPublishBusy] = useState(false);
  const [publishError, setPublishError] = useState<string | null>(null);
  const [reportStreamPath, setReportStreamPath] = useState<string | null>(null);
  const [reportMarkdown, setReportMarkdown] = useState<string | null>(null);
  const [reportHeaderImage, setReportHeaderImage] = useState<string | null>(null);
  const [reportModalOpen, setReportModalOpen] = useState(false);
  const [publishToast, setPublishToast] = useState(false);

  // Auth inline state
  const [authEmail, setAuthEmail] = useState('');
  const [authCode, setAuthCode] = useState('');
  const [authBusy, setAuthBusy] = useState(false);
  const [authError, setAuthError] = useState<string | null>(null);
  const [pendingQuestion, setPendingQuestion] = useState<string | null>(null);

  const [busy, setBusy] = useState(false);

  // ── Research-chat mode state ────────────────────────────────────
  const [rcMessages, setRcMessages] = useState<ResearchChatMsg[]>([]);
  const [rcStreaming, setRcStreaming] = useState(false);
  const [rcStreamedText, setRcStreamedText] = useState('');
  const [rcError, setRcError] = useState<string | null>(null);
  const rcAbortRef = useRef<AbortController | null>(null);
  const rcScrollRef = useRef<HTMLDivElement>(null);
  const prevResearchIdRef = useRef<number | null>(null);

  // Reset research chat when context changes
  useEffect(() => {
    const newId = researchContext?.threadId ?? null;
    if (newId !== prevResearchIdRef.current) {
      prevResearchIdRef.current = newId;
      setRcMessages([]);
      setRcStreamedText('');
      setRcError(null);
      if (rcAbortRef.current) rcAbortRef.current.abort();
    }
  }, [researchContext?.threadId]);

  // Auto-scroll research chat
  useEffect(() => {
    if (rcScrollRef.current) {
      rcScrollRef.current.scrollTop = rcScrollRef.current.scrollHeight;
    }
  }, [rcMessages, rcStreamedText]);

  const sendResearchChat = useCallback(async () => {
    if (!researchContext || rcStreaming) return;
    const text = input.trim();
    if (!text) return;

    setInput('');
    setRcError(null);
    setRcStreaming(true);
    setRcStreamedText('');

    const userMsg: ResearchChatMsg = { role: 'user', content: text };
    const updatedMessages = [...rcMessages, userMsg];
    setRcMessages(updatedMessages);

    const controller = new AbortController();
    rcAbortRef.current = controller;

    try {
      const res = await fetch(`/api/discover/${researchContext.threadId}/chat`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: text,
          history: rcMessages,
        }),
        signal: controller.signal,
      });

      if (!res.ok) {
        const json = await res.json().catch(() => ({})) as { error?: string };
        throw new Error(json.error || `Request failed: ${res.status}`);
      }

      if (!res.body) throw new Error('No response body');

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let accumulated = '';

      try {
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });

          let newlineIdx: number;
          while ((newlineIdx = buffer.indexOf('\n')) !== -1) {
            const line = buffer.slice(0, newlineIdx).trim();
            buffer = buffer.slice(newlineIdx + 1);
            if (!line) continue;
            if (line.startsWith('event:')) continue;
            if (!line.startsWith('data:')) continue;
            const payload = line.slice(5).trim();
            if (!payload) continue;
            try {
              const json = JSON.parse(payload) as { text?: string; message?: string };
              if (json.text) {
                accumulated += json.text;
                setRcStreamedText(accumulated);
              }
              if (json.message) setRcError(json.message);
            } catch { /* skip malformed */ }
          }
        }
      } finally {
        reader.releaseLock();
      }

      if (accumulated) {
        setRcMessages((prev) => [...prev, { role: 'assistant', content: accumulated }]);
      }
      setRcStreamedText('');
    } catch (e) {
      if ((e as Error).name !== 'AbortError') {
        setRcError((e as Error).message || 'Something went wrong');
      }
    } finally {
      setRcStreaming(false);
      rcAbortRef.current = null;
    }
  }, [input, rcStreaming, rcMessages, researchContext]);

  const inResearchMode = !!researchContext;

  // Show auth banner immediately when not logged in
  useEffect(() => {
    if (authLoading) return;
    if (!user && mode === 'chat') {
      setMode('auth-email');
    } else if (user && (mode === 'auth-email' || mode === 'auth-code')) {
      setMode('chat');
    }
  }, [user, authLoading]); // eslint-disable-line react-hooks/exhaustive-deps

  // Suggestions from published reports
  const [suggestions, setSuggestions] = useState<string[]>([]);

  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    fetch('/api/threads?status=published', { credentials: 'include' })
      .then((r) => r.json() as Promise<{ ok: boolean; threads: Array<{ title: string }> }>)
      .then((data) => {
        if (cancelled || !data.ok) return;
        const titles = data.threads
          .map((t) => t.title)
          .filter((t) => t && t.length > 10)
          .slice(0, 4);
        setSuggestions(titles);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [user]);

  const streamRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // ── Auto-scroll stream to bottom on new messages ────────────────
  useEffect(() => {
    const el = streamRef.current;
    if (!el) return;
    requestAnimationFrame(() => {
      el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
    });
  }, [messages.length, mode]);

  // ── Send a question — routes to /api/threads OR /api/threads/:id/messages
  const sendQuestion = useCallback(
    async (text: string) => {
      const at = Date.now();
      const userMsg: Msg = {
        kind: 'user',
        id: newId(),
        text,
        at,
      };
      setMessages((prev) => [...prev, userMsg]);
      setInput('');
      setBusy(true);

      try {
        let url: string;
        const method = 'POST';
        if (threadId === null) {
          url = '/api/threads';
        } else {
          url = `/api/threads/${threadId}/messages`;
        }

        const res = await fetch(url, {
          method,
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            body: text,
          }),
        });
        const data = (await res.json().catch(() => ({}))) as {
          threadId?: number;
          questionId?: number;
          questionCount?: number;
          streamPath?: string;
          error?: string;
        };
        if (!res.ok || typeof data.questionId !== 'number' || !data.streamPath) {
          throw new Error(data.error || `Request failed (${res.status})`);
        }

        if (typeof data.threadId === 'number') setThreadId(data.threadId);
        if (typeof data.questionCount === 'number')
          setQuestionCount(data.questionCount);

        const sysMsg: SystemMsg = {
          kind: 'system',
          id: newId(),
          question: text,
          questionId: data.questionId,
          streamPath: data.streamPath,
          at: at + 1,
        };
        setMessages((prev) => [...prev, sysMsg]);
      } catch (e) {
        const errMsg: Msg = {
          kind: 'error',
          id: newId(),
          message: e instanceof Error ? e.message : 'Could not submit question',
          at: at + 1,
        };
        setMessages((prev) => [...prev, errMsg]);
        setBusy(false);
      }
    },
    [threadId]
  );

  const remainingQuestions = THREAD_MAX_QUESTIONS - questionCount;
  const threadFull = remainingQuestions <= 0;
  const canSend = inResearchMode
    ? input.trim().length > 0 && !rcStreaming
    : input.trim().length >= 8 && !busy && !threadFull && !threadPublished;

  const handleSend = useCallback(() => {
    if (inResearchMode) {
      void sendResearchChat();
      return;
    }
    const q = input.trim();
    if (q.length < 8 || busy || threadFull || threadPublished) return;
    if (!user) {
      setPendingQuestion(q);
      setAuthError(null);
      setMode('auth-email');
      return;
    }
    onSendTriggered?.();
    void sendQuestion(q);
  }, [input, user, busy, threadFull, threadPublished, sendQuestion, onSendTriggered, inResearchMode, sendResearchChat]);

  // ── Auth step: email → code ─────────────────────────────────────
  const submitEmail = useCallback(
    async (e: FormEvent) => {
      e.preventDefault();
      setAuthError(null);
      setAuthBusy(true);
      try {
        await requestOtp(authEmail.trim().toLowerCase());
        setMode('auth-code');
      } catch (err) {
        setAuthError(err instanceof Error ? err.message : 'Could not send code');
      } finally {
        setAuthBusy(false);
      }
    },
    [authEmail, requestOtp]
  );

  const submitCode = useCallback(
    async (e: FormEvent) => {
      e.preventDefault();
      setAuthError(null);
      setAuthBusy(true);
      try {
        await verifyOtp(authEmail.trim().toLowerCase(), authCode.trim());
        setMode('chat');
        setAuthEmail('');
        setAuthCode('');
        if (pendingQuestion) {
          const q = pendingQuestion;
          setPendingQuestion(null);
          setTimeout(() => {
            void sendQuestion(q);
          }, 160);
        }
      } catch (err) {
        setAuthError(err instanceof Error ? err.message : 'Invalid code');
      } finally {
        setAuthBusy(false);
      }
    },
    [authCode, authEmail, verifyOtp, pendingQuestion, sendQuestion]
  );

  const cancelAuth = useCallback(() => {
    setMode('chat');
    setAuthError(null);
    setAuthCode('');
    if (pendingQuestion) {
      setInput(pendingQuestion);
      setPendingQuestion(null);
      requestAnimationFrame(() => textareaRef.current?.focus());
    }
  }, [pendingQuestion]);

  const backToEmailStep = useCallback(() => {
    setMode('auth-email');
    setAuthCode('');
    setAuthError(null);
  }, []);

  const handleTextareaKey = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter') {
      // In research mode: Enter sends, Shift+Enter for newline
      // In deep-dive mode: Cmd/Ctrl+Enter sends
      if (inResearchMode ? !e.shiftKey : (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        handleSend();
      }
    }
  };

  const handleSystemComplete = useCallback(() => {
    setBusy(false);
  }, []);

  const handlePickFollowup = useCallback((q: string) => {
    setInput(q);
    requestAnimationFrame(() => {
      textareaRef.current?.focus();
      textareaRef.current?.setSelectionRange(q.length, q.length);
    });
  }, []);

  const handleOpenPublish = useCallback(() => {
    setPublishError(null);
    setMode('publish');
  }, []);

  const handleCancelPublish = useCallback(() => {
    setMode('chat');
    setPublishError(null);
  }, []);

  const handleStartNewThread = useCallback(() => {
    setMessages([]);
    setThreadId(null);
    setQuestionCount(0);
    setThreadPublished(false);
    setReportStreamPath(null);
    setReportMarkdown(null);
    setReportModalOpen(false);
    setInput('');
    setMode('chat');
    setBusy(false);
  }, []);

  // ── Hydrate state from an existing thread (drafts/published nav) ───
  const hydrateFromThread = useCallback(async (loadId: number) => {
    // Clear stale content immediately so the user doesn't see old messages
    // while the fetch is in progress.
    setMessages([]);
    setMode('chat');
    setBusy(false);
    setInput('');
    setReportStreamPath(null);
    setReportMarkdown(null);
    setReportHeaderImage(null);
    setReportModalOpen(false);
    setThreadPublished(false);
    setThreadId(null);
    setQuestionCount(0);

    try {
      const res = await fetch(`/api/threads/${loadId}`, {
        credentials: 'include',
      });
      const data = (await res.json()) as {
        ok?: boolean;
        thread?: {
          id: number;
          title: string;
          status: string;
          questionCount: number;
          visibility?: string | null;
          reportMarkdown?: string | null;
          reportHeaderImage?: string | null;
        };
        turns?: Array<{
          questionId: number;
          question: string;
          insightId: number;
          headline: string;
          body: string;
          followups: string[];
          sources: Source[];
          attachments?: AttachmentMeta[];
        }>;
        error?: string;
      };
      if (!res.ok || !data.ok || !data.thread || !data.turns) {
        throw new Error(data.error ?? `Failed to load thread (${res.status})`);
      }

      setReportMarkdown(data.thread.reportMarkdown ?? null);
      setReportHeaderImage(data.thread.reportHeaderImage ?? null);
      setThreadPublished(data.thread.status !== 'open');
      setThreadId(data.thread.id);
      setQuestionCount(data.thread.questionCount);

      // Build message array from hydrated turns
      const hydratedMessages: Msg[] = [];
      for (const turn of data.turns) {
        const userMsgId = newId();
        const sysMsgId = newId();
        hydratedMessages.push({
          kind: 'user',
          id: userMsgId,
          text: turn.question,
          at: Date.now() + hydratedMessages.length * 2,
          attachments: turn.attachments,
        });
        hydratedMessages.push({
          kind: 'system',
          id: sysMsgId,
          question: turn.question,
          questionId: turn.questionId,
          streamPath: `/api/threads/${data.thread!.id}/messages/${turn.questionId}/events`,
          at: Date.now() + hydratedMessages.length * 2 + 1,
          preloaded: {
            id: turn.insightId,
            headline: turn.headline,
            body: turn.body,
            followups: turn.followups,
            sources: turn.sources,
            attachments: turn.attachments,
          },
        });
      }
      setMessages(hydratedMessages);
    } catch (e) {
      setMessages([
        {
          kind: 'error',
          id: newId(),
          message: e instanceof Error ? e.message : 'Could not load thread',
          at: Date.now(),
        },
      ]);
    }
  }, []);

  // Expose the hydration + reset callbacks to the parent via a ref-like effect
  useEffect(() => {
    if (onRequestHydration) {
      onRequestHydration(hydrateFromThread);
    }
  }, [onRequestHydration, hydrateFromThread]);

  useEffect(() => {
    if (onRequestReset) {
      onRequestReset(handleStartNewThread);
    }
  }, [onRequestReset, handleStartNewThread]);

  // Expose prefill setter to parent
  useEffect(() => {
    if (onRequestPrefill) {
      onRequestPrefill((text: string) => setInput(text));
    }
  }, [onRequestPrefill]);

  const submitPublish = useCallback(async () => {
    if (threadId === null) return;
    setPublishError(null);
    setPublishBusy(true);
    try {
      const res = await fetch(`/api/threads/${threadId}/publish`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ visibility: publishVisibility }),
      });
      const data = (await res.json().catch(() => ({}))) as {
        ok?: boolean;
        status?: string;
        reportStreamPath?: string | null;
        error?: string;
      };
      if (!res.ok || !data.ok) {
        throw new Error(data.error || `Publish failed (${res.status})`);
      }

      // Every visibility now generates a report — open the stream.
      if (data.reportStreamPath) {
        setReportStreamPath(data.reportStreamPath);
        setReportModalOpen(true);
        setMode('chat');
        setPublishToast(true);
        setTimeout(() => setPublishToast(false), 3000);
      } else {
        setThreadPublished(true);
        setMode('chat');
        setPublishToast(true);
        setTimeout(() => setPublishToast(false), 3000);
      }
    } catch (e) {
      setPublishError(
        e instanceof Error ? e.message : 'Could not publish thread'
      );
    } finally {
      setPublishBusy(false);
    }
  }, [threadId, publishVisibility]);

  const footerPortal = document.getElementById('app-footer-portal');

  // ── Render ──────────────────────────────────────────────────────
  return (
    <section className={`chat ${streamVisible ? '' : 'chat--bar-only'}`} aria-label="Deep dive">
      <div className="chat__stream" ref={streamRef} style={streamVisible ? undefined : { display: 'none' }}>
        {!inResearchMode && messages.length > 0 && (
          <div className="chat__log">
            <AnimatePresence initial={false}>
              {messages.map((m, i) => {
                if (m.kind === 'user') {
                  return <UserRow key={m.id} msg={m} />;
                }
                if (m.kind === 'error') {
                  // Find the preceding user message for retry
                  const prevUser = messages.slice(0, i).reverse().find((x) => x.kind === 'user');
                  const retryFn = prevUser && prevUser.kind === 'user'
                    ? () => { setInput(prevUser.text); textareaRef.current?.focus(); }
                    : undefined;
                  return <ErrorRow key={m.id} msg={m} onRetry={retryFn} />;
                }
                return (
                  <SystemRow
                    key={m.id}
                    msg={m}
                    isLatest={i === messages.length - 1}
                    onComplete={handleSystemComplete}
                    onPickFollowup={handlePickFollowup}
                  />
                );
              })}
            </AnimatePresence>

            {threadPublished && reportMarkdown && (
              <div className="chat__log-footer">
                <button
                  className="chat__send"
                  onClick={() => setReportModalOpen(true)}
                >
                  view report <FileText size={14} />
                </button>
                <button className="chat__send" onClick={handleStartNewThread}>
                  start a new deep dive <ArrowRight size={14} />
                </button>
              </div>
            )}
          </div>
        )}

        {/* ── Research chat messages ────────────────────── */}
        {inResearchMode && (
          <div className="rc">
            <div className="rc__header">
              <span className="rc__label">asking about</span>
              <span className="rc__title">{researchContext!.title}</span>
              <button
                className="rc__close"
                onClick={onClearResearchContext}
                aria-label="Exit research chat"
              >
                &times;
              </button>
            </div>
            <div className="rc__messages" ref={rcScrollRef}>
              {rcMessages.length === 0 && !rcStreaming && (
                <div className="rc__empty">ask anything about this research</div>
              )}
              {rcMessages.map((msg, i) => (
                <div key={i} className={`rc__msg rc__msg--${msg.role}`}>
                  {msg.content}
                </div>
              ))}
              <AnimatePresence>
                {rcStreaming && rcStreamedText && (
                  <motion.div
                    className="rc__msg rc__msg--assistant"
                    initial={{ opacity: 0, y: 3 }}
                    animate={{ opacity: 1, y: 0 }}
                  >
                    {rcStreamedText}
                  </motion.div>
                )}
                {rcStreaming && !rcStreamedText && (
                  <motion.div
                    className="rc__thinking"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                  >
                    <Loader2 size={10} className="rc__spin" />
                    thinking…
                  </motion.div>
                )}
              </AnimatePresence>
              {rcError && <div className="rc__error">{rcError}</div>}
            </div>
          </div>
        )}
      </div>

      {footerPortal && createPortal(
        <div className="chat__input-wrap">
        {/* ── Auth banner (inline above chat input) ──────── */}
        <AnimatePresence>
          {mode === 'auth-email' && (
            <motion.form
              key="auth-email"
              className="chat__auth-banner"
              onSubmit={submitEmail}
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.2 }}
            >
              <div className="chat__auth-banner-inner">
                <div className="chat__auth-note">
                  <Mail size={13} />
                  <span>Quick email to save your thinking.</span>
                  <button
                    type="button"
                    className="chat__auth-cancel"
                    onClick={cancelAuth}
                  >
                    cancel
                  </button>
                </div>
                <div className="chat__auth-row">
                  <input
                    type="email"
                    className="chat__auth-input"
                    placeholder="you@example.com"
                    value={authEmail}
                    onChange={(e) => setAuthEmail(e.target.value)}
                    autoFocus
                    required
                    autoComplete="email"
                    disabled={authBusy}
                  />
                  <motion.button
                    type="submit"
                    className="chat__send"
                    disabled={authBusy || !authEmail.trim()}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    {authBusy ? (
                      <Loader2 size={16} className="chat__send-icon chat__spin" />
                    ) : (
                      <>
                        <span>send code</span>
                        <ArrowRight size={14} />
                      </>
                    )}
                  </motion.button>
                </div>
                {authError && <div className="chat__auth-error">{authError}</div>}
              </div>
            </motion.form>
          )}

          {mode === 'auth-code' && (
            <motion.form
              key="auth-code"
              className="chat__auth-banner"
              onSubmit={submitCode}
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.2 }}
            >
              <div className="chat__auth-banner-inner">
                <div className="chat__auth-note">
                  <Mail size={13} />
                  <span>
                    Code sent to <strong>{authEmail}</strong>
                  </span>
                  <button
                    type="button"
                    className="chat__auth-cancel"
                    onClick={backToEmailStep}
                  >
                    change
                  </button>
                </div>
                <div className="chat__auth-row">
                  <input
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]{6}"
                    maxLength={6}
                    className="chat__auth-input chat__auth-input--code"
                    placeholder="123456"
                    value={authCode}
                    onChange={(e) =>
                      setAuthCode(e.target.value.replace(/\D/g, '').slice(0, 6))
                    }
                    autoFocus
                    required
                    disabled={authBusy}
                  />
                  <motion.button
                    type="submit"
                    className="chat__send"
                    disabled={authBusy || authCode.length !== 6}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    {authBusy ? (
                      <Loader2 size={16} className="chat__send-icon chat__spin" />
                    ) : (
                      <>
                        <span>verify</span>
                        <Check size={14} />
                      </>
                    )}
                  </motion.button>
                </div>
                {authError && <div className="chat__auth-error">{authError}</div>}
              </div>
            </motion.form>
          )}
        </AnimatePresence>

        {/* ── Chat input (always visible) / Publish pane ─── */}
        <AnimatePresence mode="wait">
          {mode !== 'publish' && (inResearchMode || !threadPublished) && (
            <motion.div
              key="chat"
              className="chat__input-pane"
              initial={{ opacity: 0, y: 4 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -4 }}
              transition={{ duration: 0.2 }}
            >
              {!inResearchMode && messages.length === 0 && suggestions.length > 0 && (
                <div className="chat__suggestions">
                  <span className="chat__suggestions-label">explore</span>
                  <div className="chat__suggestions-chips">
                    {suggestions.map((s) => (
                      <button
                        key={s}
                        type="button"
                        className="chat__suggestion-chip"
                        onClick={() => setInput(s)}
                      >
                        {s}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              <textarea
                ref={textareaRef}
                className={`chat__textarea${inResearchMode ? ' chat__textarea--research' : ''}`}
                placeholder={
                  inResearchMode
                    ? 'Ask this research…'
                    : threadFull
                    ? 'Deep dive complete — publish your findings.'
                    : messages.length === 0
                      ? 'Start a deep dive — what do you want to explore?'
                      : 'Keep going — go deeper...'
                }
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleTextareaKey}
                rows={inResearchMode ? 1 : 2}
                maxLength={inResearchMode ? 500 : undefined}
                disabled={inResearchMode ? rcStreaming : (busy || threadFull)}
                autoCapitalize="sentences"
                autoCorrect="on"
                spellCheck
                aria-label={inResearchMode ? 'Ask about this research' : 'Your question'}
                aria-describedby="chat-input-hint"
              />
              {!inResearchMode && input.length > 0 && input.trim().length < 8 && (
                <span id="chat-input-hint" className="chat__input-hint">
                  {8 - input.trim().length} more character{8 - input.trim().length !== 1 ? 's' : ''} needed
                </span>
              )}
              <div className="chat__toolbar">
                <div className="chat__toolbar-left">
                  {inResearchMode ? (
                    <span className="chat__kbd" aria-hidden>↵</span>
                  ) : (
                    <>
                      {threadId !== null && messages.length > 0 && (
                        <span
                          className={`chat__counter ${
                            questionCount >= 18 ? 'chat__counter--warn' : ''
                          }`}
                          aria-label={`Depth ${questionCount} of ${THREAD_MAX_QUESTIONS}`}
                          title="Deep dive progress"
                        >
                          depth {questionCount}/{THREAD_MAX_QUESTIONS}
                        </span>
                      )}
                    </>
                  )}
                </div>
                <div className="chat__toolbar-right">
                  {!inResearchMode && (
                    <span className="chat__kbd" aria-hidden>⌘↵</span>
                  )}
                  {!inResearchMode && threadId !== null && messages.length > 0 && !threadPublished && (
                    <button
                      type="button"
                      className="chat__publish-btn"
                      onClick={handleOpenPublish}
                      disabled={busy}
                      aria-label="Publish thread"
                    >
                      publish
                    </button>
                  )}
                  <motion.button
                    type="button"
                    className="chat__send"
                    onClick={handleSend}
                    disabled={!canSend}
                    whileHover={canSend ? { scale: 1.02 } : undefined}
                    whileTap={canSend ? { scale: 0.98 } : undefined}
                    aria-label="Send"
                  >
                    {(inResearchMode ? rcStreaming : busy) ? (
                      <Loader2 size={16} className="chat__send-icon chat__spin" />
                    ) : (
                      <>
                        <span>{inResearchMode ? 'ask' : 'think'}</span>
                        <ArrowRight size={14} className="chat__send-icon" />
                      </>
                    )}
                  </motion.button>
                </div>
              </div>
            </motion.div>
          )}

          {mode === 'publish' && (
            <motion.div
              key="publish"
              className="chat__input-pane chat__publish"
              initial={{ opacity: 0, y: 4 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -4 }}
              transition={{ duration: 0.2 }}
            >
              <div className="chat__auth-note">
                <Send size={13} />
                <span>ready to publish this deep dive?</span>
                <button
                  type="button"
                  className="chat__auth-cancel"
                  onClick={handleCancelPublish}
                  disabled={publishBusy}
                >
                  cancel
                </button>
              </div>
              <fieldset className="chat__publish-options" disabled={publishBusy}>
                <label className="chat__publish-option">
                  <input
                    type="radio"
                    name="publish-visibility"
                    value="private"
                    checked={publishVisibility === 'private'}
                    onChange={() => setPublishVisibility('private')}
                  />
                  <span className="chat__publish-title">private</span>
                  <span className="chat__publish-desc">
                    only you can see this
                  </span>
                </label>
                <label className="chat__publish-option">
                  <input
                    type="radio"
                    name="publish-visibility"
                    value="public"
                    checked={publishVisibility === 'public'}
                    onChange={() => setPublishVisibility('public')}
                  />
                  <span className="chat__publish-title">public</span>
                  <span className="chat__publish-desc">
                    visible to the community, attributed to you
                  </span>
                </label>
                <label className="chat__publish-option">
                  <input
                    type="radio"
                    name="publish-visibility"
                    value="anonymous"
                    checked={publishVisibility === 'anonymous'}
                    onChange={() => setPublishVisibility('anonymous')}
                  />
                  <span className="chat__publish-title">anonymous</span>
                  <span className="chat__publish-desc">
                    visible to the community, no author
                  </span>
                </label>
              </fieldset>
              {publishError && (
                <div className="chat__auth-error">{publishError}</div>
              )}
              <motion.button
                type="button"
                className="chat__send"
                onClick={submitPublish}
                disabled={publishBusy}
                whileHover={{ scale: publishBusy ? 1 : 1.02 }}
                whileTap={{ scale: publishBusy ? 1 : 0.98 }}
              >
                {publishBusy ? (
                  <Loader2 size={16} className="chat__send-icon chat__spin" />
                ) : (
                  <>
                    <span>publish</span>
                    <ArrowRight size={14} />
                  </>
                )}
              </motion.button>
            </motion.div>
          )}
        </AnimatePresence>
      </div>,
      footerPortal)}

      {/* ── Publish toast ──────────────────────────────────── */}
      <AnimatePresence>
        {publishToast && (
          <motion.div
            className="chat__toast"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            transition={{ duration: 0.25 }}
          >
            <Check size={14} /> Published
            <CelebrationBurst />
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── Report modal overlay ──────────────────────────── */}
      <AnimatePresence>
        {(reportModalOpen || reportStreamPath) && (
          <motion.div
            className="report-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
          >
            <motion.div
              className="report-overlay__card"
              initial={{ opacity: 0, y: 24, scale: 0.97 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 24, scale: 0.97 }}
              transition={{ duration: 0.3 }}
            >
              <div className="report-overlay__header">
                <span className="report__tag">RESEARCH REPORT</span>
                {reportMarkdown && (
                  <span className="report__tag report__tag--dim">published</span>
                )}
                {reportMarkdown && (
                  <button
                    className="report-overlay__close"
                    onClick={() => setReportModalOpen(false)}
                    aria-label="Close report"
                  >
                    ✕
                  </button>
                )}
              </div>

              {reportStreamPath && !reportMarkdown && (
                <ReportRow
                  streamPath={reportStreamPath}
                  onComplete={(md, img) => {
                    setReportMarkdown(md);
                    setReportHeaderImage(img ?? null);
                    setReportStreamPath(null);
                    setThreadPublished(true);
                  }}
                  onError={(msg) => {
                    setReportStreamPath(null);
                    setReportModalOpen(false);
                    setPublishError(msg || 'Report generation failed. Try publishing again.');
                    setMode('publish');
                  }}
                />
              )}

              {reportMarkdown && (
                <ReportCard markdown={reportMarkdown} headerImage={reportHeaderImage} />
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}

// ── Messages ──────────────────────────────────────────────────────────
function UserRow({ msg }: { msg: Extract<Msg, { kind: 'user' }> }) {
  return (
    <motion.div
      className="msg msg--user"
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      layout
    >
      <div className="msg__label">
        <span>YOU</span>
        <span className="msg__time">{formatTime(msg.at)}</span>
      </div>
      <p className="msg__body">{msg.text}</p>
      {msg.attachments && msg.attachments.length > 0 && (
        <div className="msg__attachments">
          {msg.attachments.map((a, i) => (
            <span key={`${a.fileName}-${i}`} className="msg__attachment-chip">
              <FileText size={11} />
              {a.fileName}
            </span>
          ))}
        </div>
      )}
    </motion.div>
  );
}

function ErrorRow({ msg, onRetry }: { msg: Extract<Msg, { kind: 'error' }>; onRetry?: () => void }) {
  return (
    <motion.div
      className="msg msg--error"
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      layout
    >
      <div className="msg__label">
        <span>SYSTEM</span>
        <span className="msg__time">error</span>
      </div>
      <p className="msg__body msg__body--error">{msg.message}</p>
      {onRetry && (
        <button type="button" className="msg__retry" onClick={onRetry}>
          <RefreshCw size={12} /> try again
        </button>
      )}
    </motion.div>
  );
}

function SystemRow({
  msg,
  isLatest,
  onComplete,
  onPickFollowup,
}: {
  msg: SystemMsg;
  isLatest: boolean;
  onComplete: () => void;
  onPickFollowup: (q: string) => void;
}) {
  if (msg.preloaded) {
    return (
      <PreloadedSystemRow
        msg={msg}
        preloaded={msg.preloaded}
        isLatest={isLatest}
        onPickFollowup={onPickFollowup}
      />
    );
  }

  return (
    <LiveSystemRow
      msg={msg}
      isLatest={isLatest}
      onComplete={onComplete}
      onPickFollowup={onPickFollowup}
    />
  );
}

function PreloadedSystemRow({
  msg: _msg,
  preloaded,
  isLatest,
  onPickFollowup,
}: {
  msg: SystemMsg;
  preloaded: PreloadedInsight;
  isLatest: boolean;
  onPickFollowup: (q: string) => void;
}) {
  return (
    <motion.div
      className="msg msg--system"
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      layout
    >
      <div className="msg__label">
        <span>SYSTEM</span>
        <span className="msg__time">saved</span>
      </div>
      <InsightCard
        insight={{
          id: preloaded.id,
          number: preloaded.id,
          headline: preloaded.headline,
          body: preloaded.body,
          confidence: 0.8,
          tags: [],
          sourceCount: preloaded.sources.length,
          mergedCount: 0,
          followups: preloaded.followups,
          sources: preloaded.sources,
        }}
        attachments={preloaded.attachments}
      />
      {isLatest && preloaded.followups.length > 0 && (
        <FollowupChips
          followups={preloaded.followups}
          onPick={onPickFollowup}
        />
      )}
    </motion.div>
  );
}

function LiveSystemRow({
  msg,
  isLatest,
  onComplete,
  onPickFollowup,
}: {
  msg: SystemMsg;
  isLatest: boolean;
  onComplete: () => void;
  onPickFollowup: (q: string) => void;
}) {
  const state = useSynthesisStream({
    questionId: msg.questionId,
    streamPath: msg.streamPath,
    question: msg.question,
    run: isLatest,
    online: true,
  });

  const prevStatusRef = useRef(state.status);
  useEffect(() => {
    const wasRunning =
      prevStatusRef.current === 'running' ||
      prevStatusRef.current === 'connecting';
    const isDone = state.status === 'complete' || state.status === 'error';
    if (wasRunning && isDone) {
      onComplete();
    }
    prevStatusRef.current = state.status;
  }, [state.status, onComplete]);

  return (
    <motion.div
      className="msg msg--system"
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      layout
    >
      <div className="msg__label">
        <span>SYSTEM</span>
        <span className="msg__time">
          {state.status === 'connecting'
            ? 'connecting…'
            : state.status === 'running'
              ? 'synthesizing…'
              : state.status === 'complete'
                ? 'complete'
                : state.status === 'error'
                  ? 'error'
                  : 'canceled'}
        </span>
      </div>
      <LayerRows
        layers={state.layers}
        modelLabel="AI"
      />
      {state.status === 'error' && state.error && (
        <p className="msg__body msg__body--error">{state.error}</p>
      )}
      {state.status === 'complete' && state.insight && (
        <>
          <InsightCard insight={state.insight} />
          {state.insight.followups.length > 0 && (
            <FollowupChips
              followups={state.insight.followups}
              onPick={onPickFollowup}
            />
          )}
        </>
      )}
    </motion.div>
  );
}

// ── Agent activity indicator ──────────────────────────────────────────

/** Derive the single current activity line from layer states. */
function deriveActivity(
  layers: SynthesisState['layers'],
  online: boolean,
  modelLabel: string,
): { stage: string; detail: string; progress: number; done: boolean } {
  const { ingest, recall, research, synthesize, publish } = layers;

  // Progress: 0–1 across all 5 stages (each worth 0.2)
  const stageWeight = (l: LayerState<LayerKey>) =>
    l.kind === 'complete' ? 1 : l.kind === 'active' ? 0.5 : 0;
  const progress =
    (stageWeight(ingest) + stageWeight(recall) + stageWeight(research) +
     stageWeight(synthesize) + stageWeight(publish)) / 5;

  if (publish.kind === 'complete') {
    const label = publish.data.insightId && publish.data.insightId < 0
      ? 'saved locally'
      : publish.data.insightId
        ? `INS #${publish.data.insightId}`
        : 'done';
    return { stage: 'Published', detail: label, progress: 1, done: true };
  }

  if (publish.kind === 'active') {
    return { stage: 'Publishing', detail: 'writing to knowledge layer…', progress, done: false };
  }

  if (synthesize.kind === 'active' || synthesize.kind === 'complete') {
    const streamed = synthesize.data.streamed?.trim() ?? '';
    if (!streamed) {
      return { stage: 'Synthesizing', detail: `${modelLabel} is thinking…`, progress, done: false };
    }
    const wordCount = streamed.split(/\s+/).length;
    return { stage: 'Writing', detail: `${modelLabel} · ${wordCount} words`, progress, done: false };
  }

  if (research.kind === 'active') {
    const { sources, totalPlanned } = research.data;
    if (sources.length === 0) {
      return { stage: 'Researching', detail: online ? 'searching the web…' : 'skipped · offline', progress, done: false };
    }
    const latest = sources[sources.length - 1];
    return { stage: 'Researching', detail: `${sources.length}/${totalPlanned} sources · ${latest.domain}`, progress, done: false };
  }

  if (research.kind === 'complete') {
    const count = research.data.sources.length;
    return { stage: 'Research done', detail: `${count} source${count !== 1 ? 's' : ''} found`, progress, done: false };
  }

  if (recall.kind === 'active') {
    return { stage: 'Recalling', detail: 'searching prior knowledge…', progress, done: false };
  }

  if (recall.kind === 'complete') {
    const { relatedInsights, similarQuestions } = recall.data;
    const total = relatedInsights.length + similarQuestions.length;
    return { stage: 'Recall done', detail: total > 0 ? `${total} related insights` : 'first exploration', progress, done: false };
  }

  if (ingest.kind === 'active' || ingest.kind === 'complete') {
    return { stage: 'Starting', detail: 'reading your question…', progress, done: false };
  }

  return { stage: 'Queued', detail: 'waiting…', progress: 0, done: false };
}

function LayerRows({
  layers,
  modelLabel,
}: {
  layers: SynthesisState['layers'];
  modelLabel: string;
}) {
  const activity = deriveActivity(layers, true, modelLabel);

  return (
    <div className="agent-activity">
      {/* Progress bar */}
      <div className="agent-activity__track">
        <motion.div
          className={`agent-activity__fill${activity.done ? ' agent-activity__fill--done' : ''}`}
          initial={{ width: 0 }}
          animate={{ width: `${Math.max(activity.progress * 100, 2)}%` }}
          transition={{ duration: 0.5, ease: 'easeOut' }}
        />
      </div>

      {/* Status line */}
      <div className="agent-activity__row">
        {!activity.done ? (
          <motion.span
            className="agent-activity__pulse"
            animate={{ opacity: [1, 0.3, 1] }}
            transition={{ duration: 1.4, repeat: Infinity, ease: 'easeInOut' }}
          />
        ) : (
          <span className="agent-activity__check">
            <Check size={10} strokeWidth={3} />
          </span>
        )}
        <span className="agent-activity__stage">{activity.stage}</span>
        <AnimatePresence mode="wait">
          <motion.span
            key={activity.detail}
            className="agent-activity__detail"
            initial={{ opacity: 0, y: 4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -4 }}
            transition={{ duration: 0.2 }}
          >
            {activity.detail}
          </motion.span>
        </AnimatePresence>
      </div>
    </div>
  );
}

// ── Insight card ──────────────────────────────────────────────────────
function InsightCard({
  insight,
  attachments,
}: {
  insight: NonNullable<SynthesisState['insight']>;
  attachments?: AttachmentMeta[];
}) {
  return (
    <motion.article
      className="insight"
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
    >
      <div className="insight__head">
        <span className="insight__tag">INSIGHT</span>
        <span className="insight__merged">INS #{insight.number}</span>
      </div>
      <h3 className="insight__headline">{insight.headline}</h3>
      <p className="insight__body">
        {insight.sources.length > 0
          ? renderCitations(insight.body, insight.sources)
          : insight.body}
      </p>
      {attachments && attachments.length > 0 && (
        <div className="insight__attachments">
          <span className="insight__sources-label">attached files</span>
          {attachments.map((a, i) => (
            <span key={`${a.fileName}-${i}`} className="insight__attachment">
              <FileText size={12} />
              <span className="insight__attachment-name">{a.fileName}</span>
              <span className="insight__attachment-type">
                {a.mimeType.split('/').pop()}
              </span>
            </span>
          ))}
        </div>
      )}
      {insight.sources.length > 0 && (
        <div className="insight__sources">
          <span className="insight__sources-label">sources</span>
          {insight.sources.map((s, i) => (
            <a
              key={`${s.url}-${i}`}
              href={s.url}
              target="_blank"
              rel="noopener noreferrer"
              className="insight__source"
            >
              <span className="insight__source-domain">{s.domain}</span>
              <span className="insight__source-title">{s.title}</span>
            </a>
          ))}
        </div>
      )}
    </motion.article>
  );
}

// ── Follow-up chips ───────────────────────────────────────────────────
function FollowupChips({
  followups,
  onPick,
}: {
  followups: string[];
  onPick: (q: string) => void;
}) {
  return (
    <div className="followups">
      <span className="followups__label">go deeper</span>
      <div className="followups__list">
        {followups.map((q, i) => (
          <button
            key={`${i}-${q}`}
            type="button"
            className="followups__chip"
            onClick={() => onPick(q)}
          >
            {q}
          </button>
        ))}
      </div>
    </div>
  );
}

// ── Report stream row (fires when user hits public/anonymous publish) ─
function ReportRow({
  streamPath,
  onComplete,
  onError,
}: {
  streamPath: string;
  onComplete: (markdown: string, headerImage?: string | null) => void;
  onError?: (message?: string) => void;
}) {
  const [markdown, setMarkdown] = useState('');
  const [stages, setStages] = useState<
    Record<'compile' | 'write' | 'publish', LayerKind>
  >({ compile: 'pending', write: 'pending', publish: 'pending' });
  const [error, setError] = useState<string | null>(null);
  const doneRef = useRef(false);

  useEffect(() => {
    doneRef.current = false;
    setMarkdown('');
    setStages({ compile: 'pending', write: 'pending', publish: 'pending' });
    setError(null);

    const es = new EventSource(streamPath, { withCredentials: true });

    const onStageBegin = (e: MessageEvent) => {
      try {
        const p = JSON.parse(e.data) as { stage?: keyof typeof stages };
        if (p.stage) {
          setStages((s) => ({ ...s, [p.stage!]: 'active' as LayerKind }));
        }
      } catch {
        /* ignore */
      }
    };
    const onStageEnd = (e: MessageEvent) => {
      try {
        const p = JSON.parse(e.data) as { stage?: keyof typeof stages };
        if (p.stage) {
          setStages((s) => ({ ...s, [p.stage!]: 'complete' as LayerKind }));
        }
      } catch {
        /* ignore */
      }
    };
    const onToken = (e: MessageEvent) => {
      try {
        const p = JSON.parse(e.data) as { text?: string };
        if (typeof p.text === 'string') setMarkdown((m) => m + p.text);
      } catch {
        /* ignore */
      }
    };
    const onDone = (e: MessageEvent) => {
      doneRef.current = true;
      try {
        const p = JSON.parse(e.data) as {
          report?: { markdown: string; headerImage?: string | null };
        };
        if (p.report?.markdown) onComplete(p.report.markdown, p.report.headerImage);
      } catch {
        /* ignore */
      }
      es.close();
    };
    // Server-sent errors arrive as 'server-error' (renamed to avoid
    // collision with EventSource's native 'error' event).
    const onServerError = (e: MessageEvent) => {
      let msg = 'Report generation failed';
      try {
        const p = JSON.parse(e.data) as { message?: string };
        if (p.message) msg = p.message;
      } catch { /* use default */ }
      setError(msg);
      es.close();
      onError?.(msg);
    };

    // Native transport/connection errors (401, network drop, etc.)
    es.onerror = () => {
      if (doneRef.current) return;
      const msg = 'Connection to report stream failed — try again.';
      setError(msg);
      es.close();
      onError?.(msg);
    };

    es.addEventListener('stage-begin', onStageBegin as EventListener);
    es.addEventListener('stage-end', onStageEnd as EventListener);
    es.addEventListener('token', onToken as EventListener);
    es.addEventListener('done', onDone as EventListener);
    es.addEventListener('server-error', onServerError as EventListener);

    return () => {
      es.close();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [streamPath]);

  return (
    <motion.div
      className="msg msg--system"
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      layout
    >
      <div className="msg__label">
        <span>REPORT</span>
        <span className="msg__time">curating…</span>
      </div>
      <div className="layers">
        <ReportLayerRow step="01" label="COMPILE" kind={stages.compile} />
        <ReportLayerRow step="02" label="WRITE" kind={stages.write} />
        <ReportLayerRow step="03" label="PUBLISH" kind={stages.publish} />
      </div>
      {error && <p className="msg__body msg__body--error">{error}</p>}
      {markdown && (
        <pre className="report__stream">
          {markdown}
          {!doneRef.current && <span className="report__caret">▌</span>}
        </pre>
      )}
    </motion.div>
  );
}

function ReportLayerRow({
  step,
  label,
  kind,
}: {
  step: string;
  label: string;
  kind: LayerKind;
}) {
  return (
    <div className={`report-layer report-layer--${kind}`}>
      {kind === 'complete' ? (
        <span className="agent-activity__check">
          <Check size={10} strokeWidth={3} />
        </span>
      ) : kind === 'active' ? (
        <motion.span
          className="agent-activity__pulse"
          animate={{ opacity: [1, 0.3, 1] }}
          transition={{ duration: 1.4, repeat: Infinity, ease: 'easeInOut' }}
        />
      ) : (
        <span className="agent-activity__pulse" style={{ opacity: 0.3, boxShadow: 'none' }} />
      )}
      <span className="agent-activity__stage">{step} · {label}</span>
      <span className="agent-activity__detail">
        {kind === 'pending' ? '' : kind === 'active' ? 'running…' : 'done'}
      </span>
    </div>
  );
}

// ── Export / share helpers ────────────────────────────────────────────

function extractTitle(md: string): string {
  const m = md.match(/^#\s+(.+)/m);
  return m ? m[1].trim() : 'Research Report';
}

function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

function exportMarkdown(md: string) {
  const title = extractTitle(md);
  downloadBlob(new Blob([md], { type: 'text/markdown' }), `${title}.md`);
}

function exportJSON(md: string) {
  const title = extractTitle(md);
  const payload = { title, format: 'ctfhub-report-v1', markdown: md };
  downloadBlob(
    new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' }),
    `${title}.json`,
  );
}

function exportHTML(md: string) {
  const title = extractTitle(md);
  const tmp = document.createElement('div');
  // Render the already-mounted prose — grab from DOM for fidelity
  const prose = document.querySelector('.report__prose');
  tmp.innerHTML = prose ? prose.innerHTML : `<pre>${md}</pre>`;
  const html = `<!DOCTYPE html>
<html lang="en"><head><meta charset="utf-8"><title>${title}</title>
<style>body{font-family:system-ui,sans-serif;max-width:720px;margin:40px auto;padding:0 20px;line-height:1.7;color:#1a1a1a}h1{font-size:28px}h2{font-size:13px;letter-spacing:1.5px;text-transform:uppercase;color:#666;border-bottom:1px solid #e0e0e0;padding-bottom:6px}a{color:#1a1a1a}hr{border:none;border-top:1px solid #e0e0e0;margin:24px 0}</style>
</head><body>${tmp.innerHTML}</body></html>`;
  downloadBlob(new Blob([html], { type: 'text/html' }), `${title}.html`);
}

async function nativeShare(md: string) {
  if (!navigator.share) return;
  const title = extractTitle(md);
  try {
    await navigator.share({ title, text: `${title} — research via Craft The Future`, url: window.location.href });
  } catch { /* user cancelled */ }
}

function shareURL(platform: 'x' | 'linkedin' | 'substack' | 'bluesky', md: string) {
  const title = extractTitle(md);
  const pageURL = window.location.href;
  const text = `${title} — research report via CTFHub`;
  let url = '';
  switch (platform) {
    case 'x':
      url = `https://x.com/intent/post?text=${encodeURIComponent(text)}&url=${encodeURIComponent(pageURL)}`;
      break;
    case 'linkedin':
      url = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(pageURL)}`;
      break;
    case 'substack':
      url = `https://substack.com/notes/share?url=${encodeURIComponent(pageURL)}&title=${encodeURIComponent(title)}&body=${encodeURIComponent(text)}`;
      break;
    case 'bluesky':
      url = `https://bsky.app/intent/compose?text=${encodeURIComponent(`${text}\n${pageURL}`)}`;
      break;
  }
  window.open(url, '_blank', 'noopener,noreferrer');
}

// ── Finished report card ──────────────────────────────────────────────
function ReportCard({ markdown, headerImage }: { markdown: string; headerImage?: string | null }) {
  const [showActions, setShowActions] = useState(false);

  return (
    <motion.article
      className="report"
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.55 }}
    >
      <div className="report__head">
        <span className="report__tag">RESEARCH REPORT</span>
        <span className="report__tag report__tag--dim">published</span>
        <button
          className="report__actions-toggle"
          onClick={() => setShowActions((s) => !s)}
          aria-label="Export or share"
          aria-expanded={showActions}
        >
          {showActions ? <Share2 size={13} /> : <Share2 size={13} />}
          <span>{showActions ? 'close' : 'export / share'}</span>
        </button>
      </div>

      <AnimatePresence>
        {showActions && (
          <motion.div
            className="report__toolbar"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.2 }}
          >
            <div className="report__toolbar-group">
              <span className="report__toolbar-label"><Download size={11} /> Export</span>
              <button className="report__btn" onClick={() => exportMarkdown(markdown)}>MD</button>
              <button className="report__btn" onClick={() => exportHTML(markdown)}>HTML</button>
              <button className="report__btn" onClick={() => exportJSON(markdown)}>JSON</button>
            </div>
            <div className="report__toolbar-group">
              <span className="report__toolbar-label"><Share2 size={11} /> Share</span>
              {!!navigator.share && (
                <button className="report__btn report__btn--accent" onClick={() => nativeShare(markdown)}>Share</button>
              )}
              <button className="report__btn" onClick={() => shareURL('x', markdown)}>X</button>
              <button className="report__btn" onClick={() => shareURL('linkedin', markdown)}>LinkedIn</button>
              <button className="report__btn" onClick={() => shareURL('substack', markdown)}>Substack</button>
              <button className="report__btn" onClick={() => shareURL('bluesky', markdown)}>Bluesky</button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {headerImage && (
        <div className="report__hero">
          <img src={headerImage} alt="" className="report__hero-img" />
        </div>
      )}

      <div className="report__prose">
        <ReactMarkdown>{markdown}</ReactMarkdown>
      </div>
    </motion.article>
  );
}
